import { Modal } from "@circle-react-uikit/ModalV2";
import { Typography } from "@circle-react-uikit/Typography";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { Button } from "@circleco/compass/components/Button";
import { format } from "date-fns";

import { t } from "@/i18n-js/instance";

import { useCanDowngrade } from "../hooks/useCanDowngrade";
import { isDowngrade, isIntervalDowngrade } from "../planActionHelpers";
import type { UpgradePlan } from "../types";
import { UpgradePlanModalBody } from "../UpgradePlanModalBody";
import { UpgradePlanModalFooter } from "../UpgradePlanModalFooter";
import { UpgradePlanModalHeader } from "../UpgradePlanModalHeader";
import { BlockingFeatures } from "./BlockingFeatures";
import { LegacyEnterpriseBanner } from "./LegacyEnterpriseBanner";

interface ScheduledDowngradeConfirmationModalProps {
  hideModal: () => void;
  onSubmitHandler: () => void;
  plan: UpgradePlan;
}

export const ScheduledDowngradeConfirmationModal = ({
  hideModal,
  onSubmitHandler,
  plan,
}: ScheduledDowngradeConfirmationModalProps) => {
  const { currentCommunity } = usePunditUserContext();

  const { data: canDowngradeData, isPending: isCanDowngradeLoading } =
    useCanDowngrade(plan.id);

  const formattedDate = format(
    new Date(currentCommunity?.subscription.current_period_end || ""),
    "MMM dd, yyyy",
  );

  const blockingFeatures = canDowngradeData?.blocking_features || [];

  const getI18nRoot = () => {
    if (isIntervalDowngrade(plan?.plan_action)) {
      return "pricing_plans.upgrade_modal.interval_switch";
    }
    if (isDowngrade(plan?.plan_action)) {
      return "pricing_plans.upgrade_modal.downgrade";
    }
    // Fallback to interval_switch for any other case
    return "pricing_plans.upgrade_modal.interval_switch";
  };

  const i18nRoot = getI18nRoot();

  return (
    <>
      <UpgradePlanModalHeader
        title={t([i18nRoot, "scheduled_downgrade.title"])}
      >
        <Modal.Title>{t([i18nRoot, "scheduled_downgrade.title"])}</Modal.Title>

        <Modal.HeaderActions>
          <Modal.CloseButton onClick={hideModal} />
        </Modal.HeaderActions>
      </UpgradePlanModalHeader>
      <UpgradePlanModalBody>
        <div className="mb-6 space-y-2">
          <Typography.BodyMd>
            {t(`${i18nRoot}.scheduled_downgrade.plan_expires`)}{" "}
            <strong className="font-semibold text-primary">
              {formattedDate}
            </strong>
            {!isIntervalDowngrade(plan?.plan_action) && (
              <>. {t(`${i18nRoot}.scheduled_downgrade.consequences`)}</>
            )}
          </Typography.BodyMd>
        </div>
        {!isIntervalDowngrade(plan?.plan_action) && (
          <BlockingFeatures
            blockingFeatures={blockingFeatures}
            i18nRoot={i18nRoot}
            isLoading={isCanDowngradeLoading}
          />
        )}
        <LegacyEnterpriseBanner
          message={t("pricing_plans.legacy_enterprise_banner.downgrade_modal")}
        />
      </UpgradePlanModalBody>
      <UpgradePlanModalFooter
        className="mt-5"
        secondaryAction={{ label: t("cancel"), onClick: hideModal }}
        primaryAction={{
          label: t(`${i18nRoot}.scheduled_downgrade.actions.submit`),
          onClick: onSubmitHandler,
          disabled: isCanDowngradeLoading,
        }}
      >
        <div className="flex justify-end gap-x-3">
          <Button variant="outline" size="lg" onClick={hideModal}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={onSubmitHandler}
            disabled={isCanDowngradeLoading}
          >
            {t(`${i18nRoot}.scheduled_downgrade.actions.submit`)}
          </Button>
        </div>
      </UpgradePlanModalFooter>
    </>
  );
};
